<template>
  <div id="warrant-details">
    <v-row>
      <v-col cols="3">
        <v-select
          v-model="editedItem.status"
          id="edited-item-status"
          :items="filteredWarTransStatus"
          item-text="description"
          item-value="name"
          item-disabled="disabled"
          label="Status"
          @change="onStatusChange"
        >
        </v-select>
      </v-col>
      <v-col cols="2">
        <v-checkbox
          v-model="editedItem.isNonPayable"
          label="Non-Payable"
        ></v-checkbox>
      </v-col>
      <v-col cols="3">
        <PaymentNumberInput class="warrant paymentNo"
          v-model.number="editedItem.payment"
          :fund="getFundId"
          :fiscalYear="editedItem.fiscalYear"
          :isNewItem="isNewItem"
          :itemStatus="editedItem.status"
          :baseCrudKey="baseCrudKey"
        />
      </v-col>
      <v-col cols="3">
        <ClarionDateControl
        v-model="editedItem.paidCancelledDate"
         :isIsoDate="true"
         :isToggle="false"
         :label="`Paid / Cancelled date`" />
      </v-col>
      <v-col cols="3" v-if="editedItem.status === 'CANCELLED'">
        <v-checkbox
          v-model="editedItem.cancelledByStatute"
          label="Cancelled by Statute"
        ></v-checkbox>
      </v-col>
    </v-row>
    <v-row v-if="isClearingFund">
      <v-col cols="2">
        <v-select
          v-model="editedItem.clearingFundNumber"
          id="edited-item-clearingFundNumber"
          :items="clearingFunds"
          label="Clearing Fund"
        >
        </v-select>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import { mapGetters, mapState, mapMutations } from 'vuex';
import ClarionDateControl from '../../common/ClarionDateControl.vue';
import PaymentNumberInput from './PaymentNumberIput.vue';
import WarrantAppropriationsMixin from './WarrantAppropiations.mixin';
import { extractClearingFundNumber } from './warTransaction.util';
import { toIsoDate, daysOffset } from '../../../util/shared/tmc-global';

export default {
  name: 'WarrantsDetails',
  mixins: [WarrantAppropriationsMixin],
  components: {
    ClarionDateControl,
    PaymentNumberInput,
  },
  props: {
    baseCrudKey: {
      type: String,
      default: 'war-dialog',
    },
  },
  data: () => ({
    checkbox: true,
  }),
  computed: {
    ...mapGetters('base/crud', ['criteria', 'item', 'index']),
    ...mapGetters(['todaysDate']),
    ...mapState({
      warSettingItem: (state) => state.SystemConfig.warSettingItem,
      clearingFunds() {
        const clearingFundNumWithBank = (this.warSettingItem
        && this.warSettingItem.clearingFundList)
         || [];
        const clearingFundNumOnly = extractClearingFundNumber(clearingFundNumWithBank);
        return ['', ...clearingFundNumOnly];
      },
      transactionTypes: (state) => state.enums.WarTransTypeEnum,
      warTransStatus: (state) => state.enums.WarTransStatusEnum,
      nextPaymentNumber: (state) => state.war.transaction.nextPaymentNumber,
      warrantRegPolling: (state) => state.war.transaction.warrantRegPolling,
    }),
    isClearingFund() {
      return this.warSettingItem && this.warSettingItem.clearingFund;
    },
    isNewItem() {
      return this.index(this.baseCrudKey) === -1;
    },
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    editedItem: {
      get() {
        return this.item(this.baseCrudKey);
      },
      set(value) {
        this.setItem([this.baseCrudKey, value]);
      },
    },
    getFundId() {
      return this.editedItem.details
      && this.editedItem.details[0]
      && this.editedItem.details[0].fund
      && this.editedItem.details[0].fund._id;
    },
    filteredWarTransStatus() {
      const registerDate = this.editedItem && this.editedItem.registerDate;
      if (!registerDate) return this.warTransStatus;
      const refDate = this.todaysDate ? new Date(this.todaysDate) : new Date();
      const sevenDaysAgo = toIsoDate(daysOffset(7, refDate));
      const isMoreThanSevenDays = registerDate < sevenDaysAgo;
      return (this.warTransStatus || []).map((status) => {
        if (status.name === 'VOID' && isMoreThanSevenDays) {
          return { ...status, disabled: true };
        }
        return status;
      });
    },
  },
  beforeDestroy() {
    if (this.warrantRegPolling) {
      clearInterval(this.warrantRegPolling);
      this.setWarrantRegPolling(undefined);
    }
  },
  methods: {
    ...mapMutations('war/transaction', [
      'setWarrantRegPolling',
    ]),
    onStatusChange() {
      if (this.editedItem.status === 'PAID' && !(this.getFundId == null)) {
        this.editedItem.paidCancelledDate = this.todaysDate;
        this.editedItem.cancelledByStatute = false;
        const assignPaymentToEft = this.warSettingItem
          && this.warSettingItem.assignPaymentNumberToEftWarrants;
        if (this.editedItem.isEftCode && !assignPaymentToEft) {
          this.editedItem.payment = null;
        } else {
          this.setWarrantRegPolling(undefined);
          const warrantTransPolling = setInterval(
            this.determineNextPaymentNo(
              {
                fund: this.getFundId,
                fiscalYear: this.editedItem.fiscalYear,
              },
            ),
            1200,
          );
          this.setWarrantRegPolling(warrantTransPolling);
        }
      } else if (this.editedItem.status === 'VOID') {
        this.editedItem.paidCancelledDate = null;
        this.editedItem.cancelledByStatute = false;
        this.updateAmountPaymentNo();
      } else if (this.editedItem.status === 'OUTSTANDING') {
        this.editedItem.paidCancelledDate = null;
        this.editedItem.payment = null;
        this.editedItem.cancelledByStatute = false;
      } else if (this.editedItem.status === 'CANCELLED') {
        if (!this.editedItem.paidCancelledDate) {
          this.editedItem.paidCancelledDate = this.todaysDate;
        }
        this.editedItem.payment = null;
      }
    },
    updateAmountPaymentNo() {
      this.editedItem.amount = 0.00;
      this.editedItem.payment = null;
      this.editedItem.details = (this.editedItem.details || []).map((item) => {
        const updatedItem = { ...item };
        updatedItem.amount = 0.00;
        return updatedItem;
      });
    },
  },
};
</script>
